import loadable from '@loadable/component'
import React from 'react'
import toast from 'react-hot-toast'
import { useNavigate } from 'react-router-dom'

import DropDownMenu, { MenuOption } from '@/common/components/DropDownMenu'
import EllipsisIcon from '@/common/components/icons/EllipsisIcon'
import useApis from '@/common/hooks/useApis'
import useI18n from '@/common/hooks/useI18n'
import useLoadingOverlay from '@/common/hooks/useLoadingOverlay'
import useSwitch from '@/common/hooks/useSwitch'
import { resolvePath } from '@/common/utils/url'
import useBrokerListings from '@/modules/broker/hooks/useBrokerListings'
import {
  SEE_PLACE_DETAILS,
  CANCEL_REQUEST,
  CANCEL_REQUEST_MODAL_TITLE,
  CANCEL_REQUEST_MODAL_DESCRIPTION,
  CANCEL_REQUEST_SUCCESS_MESSAGE,
} from '@/modules/me/pages/BrokerListingsPage/translation'
import useRoom from '@/modules/property/hooks/useRoom'
import { ROOM_DETAIL_PATH } from '@/modules/property/paths'

const ConfirmModal = loadable(
  () => import('@/common/components/ConfirmModal'),
  { ssr: false }
)

interface BrokerListingsActionProps {
  brokerListing: RentAPerfect.BrokerListing
}

const BrokerListingsAction = ({ brokerListing }: BrokerListingsActionProps) => {
  const { t } = useI18n()
  const { apis } = useApis()
  const navigate = useNavigate()
  const {
    value: isOpenConfirmModal,
    on: openConfirmModal,
    off: closeConfirmModal,
  } = useSwitch()
  const loadingOverlay = useLoadingOverlay()

  const { Room: room } = brokerListing

  const { refetchAll: refetchAllBrokerListings } = useBrokerListings()
  const { refetch: refetchRoom } = useRoom({
    roomId: room?.id,
    queryOptions: { enabled: false },
  })

  const onCancelRequest = () => {
    loadingOverlay.open()

    apis.broker
      .deleteListing({ brokerListingId: brokerListing.id })
      .then(async () => {
        await refetchAllBrokerListings()
        refetchRoom()
        toast.success(t(CANCEL_REQUEST_SUCCESS_MESSAGE))
        closeConfirmModal()
      })
      .finally(() => {
        loadingOverlay.close()
      })
  }

  return (
    <>
      <DropDownMenu
        options={
          [
            room
              ? {
                  label: SEE_PLACE_DETAILS,
                  onClick: () =>
                    navigate(
                      resolvePath(ROOM_DETAIL_PATH, { roomId: room.id })
                    ),
                }
              : null,
            { label: CANCEL_REQUEST, onClick: openConfirmModal },
          ].filter(Boolean) as MenuOption[]
        }
        dropDownProps={{
          labelClassName: 'text-12 text-medium text-nowrap',
          component: (
            <div className='px-8'>
              <EllipsisIcon size={12} />
            </div>
          ),
          alignment: 'right',
        }}
      />
      <ConfirmModal
        title={CANCEL_REQUEST_MODAL_TITLE}
        description={CANCEL_REQUEST_MODAL_DESCRIPTION}
        isOpen={isOpenConfirmModal}
        onClose={closeConfirmModal}
        onSubmit={onCancelRequest}
      />
    </>
  )
}

export default BrokerListingsAction
